import _ from "lodash";
import uuid from "uuid";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import {
  DEFAULT_COLUMN_FIELDS,
  DEFAULT_CONTROLS,
} from "views/Pages/Finding/constants";
import { StyledDialog, dialogClasses } from "views/Pages/Finding/styles";
import ActionDialog from "views/Components/ActionDialog";
import API from "lib/api/API2";
import Logger from "lib/logger";

const PREFIX = "FindingPresets";

const classes = {
  inlineFormControl: `${PREFIX}-inlineFormControl`,
  quickSelectItem: `${PREFIX}-quickSelectItem`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.shape.padding,
  marginRight: theme.shape.padding,
  [`& .${classes.inlineFormControl}`]: {
    minHeight: 43,
    minWidth: 250,
  },
  [`& .${classes.quickSelectItem}`]: {
    textTransform: "capitalize",
    backgroundColor: theme.palette.background.paper,
  },
}));

const isGlobal = (query) => !query?.orgId;

const FindingPresets = ({
  controlInterface,
  displayMessage,
  orgId,
  setActiveFields,
}) => {
  const logger = Logger("FindingPresets");

  const defaultQueries = [
    { name: "All Findings", queryParams: Object.values(DEFAULT_CONTROLS) },
  ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [queries, setQueries] = useState(defaultQueries);
  const [queryPendingDeletion, setQueryPendingDeletion] = useState(null);
  const [viewingQueries, setViewingQueries] = useState(defaultQueries);
  const [viewingQuery, setViewingQuery] = useState("All Findings");

  const fetchPresets = async () => {
    setLoading(true);
    setQueries(defaultQueries);
    setViewingQueries(defaultQueries);
    const params = { namespace: "finding" };
    try {
      const api = new API();
      const response = await api.get("query", params, []);
      let data = response?.data || [];
      data = _.orderBy(data, ["orgId", "name"], ["asc", "asc"]);
      data = defaultQueries.concat(data);
      setQueries(data);
      setViewingQueries(data);
    } catch (err) {
      logger.error(err);
      displayMessage({
        severity: "error",
        message: `Failed to load search presets: ${err}`,
        noAutoHide: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Re-fetch when we open the dialog
    if (isDialogOpen) {
      fetchPresets();
    }
  }, [isDialogOpen]);

  useEffect(() => {
    setViewingQuery("All Findings");
    fetchPresets();
  }, [orgId]);

  const handleDeleteQuery = async () => {
    if (!queryPendingDeletion) return;
    try {
      const api = new API();
      await api.delete("query", queryPendingDeletion.id);
      displayMessage({
        severity: "success",
        message: "Successfully deleted search preset",
      });
    } catch (err) {
      logger.error(err);
      displayMessage({
        severity: "error",
        message: `Failed to delete search preset: ${err}`,
        noAutoHide: true,
      });
    } finally {
      setQueryPendingDeletion(null);
      fetchPresets();
    }
  };

  const handleSelectQuery = (query) => {
    // Collect new controls
    const newControls = {};
    const queryParams = query.queryParams || [];
    for (const param of queryParams) {
      const id = String(uuid.v4());
      newControls[id] = { ...param, uuid: id };
    }
    const transforms = query.resultParams?.transform || [];
    for (const param of transforms) {
      const id = String(uuid.v4());
      newControls[id] = { ...param, uuid: id };
    }
    // TODO future work save/restore orderBy as well

    // Update our state
    const outputFields = query.resultParams?.outputFields;
    if (outputFields && outputFields.length) {
      setActiveFields(outputFields);
    } else {
      setActiveFields(DEFAULT_COLUMN_FIELDS);
    }
    controlInterface.restore(newControls);
    setViewingQuery(query.name);
    setIsDialogOpen(false);
  };

  const handleTextFilterChange = (event) => {
    const searchText = event.target.value.toLowerCase();
    if (searchText && searchText !== "") {
      setViewingQueries(
        queries.filter((q) => q.name.toLowerCase().includes(searchText))
      );
    } else {
      setViewingQueries(queries);
    }
  };

  return (
    <StyledBox>
      <FormControl className={classes.inlineFormControl}>
        <InputLabel id="datetime-select-label">Search Presets</InputLabel>
        {/*
        This `Select` is a dummy component just to get the nice label
        styling that comes with it. It actually acts like a button though.
        */}
        <Select
          datacy="findingPresetsSelect"
          id="datetime-select"
          value="1"
          labelId="datetime-select-label"
          label="Search Presets"
          className={classes.quickSelectItem}
          variant={"outlined"}
          IconComponent={() => null}
          SelectDisplayProps={{
            onClick: () => setIsDialogOpen(true),
            onMouseDown: (e) => e.preventDefault(),
          }}
        >
          <MenuItem value="1">{viewingQuery}</MenuItem>
        </Select>
      </FormControl>
      <StyledDialog
        datacy="presetsDialog"
        onClose={() => setIsDialogOpen(false)}
        open={isDialogOpen}
        maxWidth={"md"}
      >
        <div className={dialogClasses.presetDialog}>
          <div className={dialogClasses.titleOuterContainer}>
            <div className={dialogClasses.titleContainer}>
              <TextField
                datacy="presetsSearchInput"
                label={"Search Presets"}
                onChange={handleTextFilterChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <IconButton
              datacy="presetsCloseButton"
              onClick={() => setIsDialogOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {loading ? (
            <div
              className={`${dialogClasses.queryContainer} ${dialogClasses.loadingContainer}`}
            >
              <CircularProgress />
            </div>
          ) : (
            <div
              className={dialogClasses.queryContainer}
              datacy="presetsListContainer"
            >
              {viewingQueries &&
                viewingQueries.map((q, i) => (
                  <div
                    key={i}
                    className={dialogClasses.queryNameContainer}
                    datacy={`presetItem${i}`}
                    onClick={() => handleSelectQuery(q)}
                  >
                    <p>{q.name}</p>
                    {isGlobal(q) ? (
                      <Chip
                        datacy="globalIndicatorChip"
                        icon={<PublicIcon />}
                        label={"Global"}
                      />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          setQueryPendingDeletion(q);
                        }}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      </StyledDialog>
      <ActionDialog
        title="Delete"
        open={!!queryPendingDeletion}
        description="Are you sure you want to delete this search preset?"
        actions={[
          {
            title: "Cancel",
            action: () => setQueryPendingDeletion(null),
          },
          {
            title: "Delete",
            action: handleDeleteQuery,
            variant: "contained",
          },
        ]}
      />
    </StyledBox>
  );
};

FindingPresets.propTypes = {
  controlInterface: PropTypes.object.isRequired,
  displayMessage: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  setActiveFields: PropTypes.func.isRequired,
};

export default FindingPresets;
